// @flow
import styled, { css } from "styled-components";
import React, { Component, useEffect, useRef, useState } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { Button, Loader, Segment, Table, Label } from "semantic-ui-react";
import { listBillingForecast } from "../../../redux/modules/billing-forecast";
import { noZero } from "../../../helpers/billing";
import { Link } from "react-router-dom";
import { reverse, routes } from "../../../routes";
import {
  amountToCurrency,
  amountToCurrencyOrEmpty,
  getAppCurrency,
} from "../../../helpers/currency";
import { apirequest } from "../../../helpers/api-client";
import { ErrorWrap } from "../../../helpers/error";
import { DownloadLink } from "../../../helpers/download-link";
import { RoleRequired } from "../../auth/role-required";
import { permissions } from "../../../constant/permissions";
import { t } from "../../../constant/translations";

type BudgetRowProps = {
  entity: Object,
  models: [Object],
  displayCalculated: boolean,
  editing: boolean,
};

const Controller = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
`;

const FlexGrow = styled.div`
  flex-grow: 1;
`;

const BreakdownInput = styled.input`
  background-color: transparent;
  border: none;
  width: 100%;

  &:focus {
    outline: none;
  }
`;

const FocusableCell = styled(Table.Cell)`
  [disabled] {
    color: inherit;
  }

  ${(props) =>
    props.$isFocused &&
    css`
      box-shadow: 3px 3px 5px ${props.theme.primary};
      border-radius: 6px 6px 6px 6px;
    `}
  ${(props) =>
    props.$hasError &&
    css`
      background-color: ${props.theme.errorBg};
    `}
`;

const EditableCell = ({ value, editing, mountIdx, onChange, error }) => {
  const inputRef = useRef(null);
  const [focused, setFocus] = useState(false);
  useEffect(() => {
    if (mountIdx === "0-0") {
      inputRef.current.focus();
    }
  }, [editing]);

  return (
    <FocusableCell
      $isFocused={focused}
      $hasError={error}
      onClick={() => {
        editing && inputRef.current.focus();
      }}
    >
      <BreakdownInput
        value={value}
        disabled={!editing ? "disabled" : ""}
        type={editing ? "number" : ""}
        step="0.01"
        onFocus={(event) => {
          event.target.select();
          setFocus(true);
        }}
        onBlur={() => {
          setFocus(false);
        }}
        ref={inputRef}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
    </FocusableCell>
  );
};

const BudgetList = (props) => {
  const [displayCalculated, setDisplayCalculated] = useState(true);
  const [editing, setEditing] = useState(false);
  const [changelist, setChangeList] = useState({});
  const [loadingState, setLoadingState] = useState(false);
  const [error, setError] = useState(null);
  const [filterBy, setFilterBy] = useState(null);

  const changelistID = (entity, model) => {
    return entity.estate.id.toString() + model.id.toString();
  };

  const onFieldChange = (entity, model, value) => {
    if (value < 0) {
      return;
    }

    // Calculate amount first
    const modelPrice = parseFloat(model.price);
    const modelMinPrice = parseFloat(model.minimum);
    const amount = value ? Math.max(value * modelPrice, modelMinPrice) : 0;

    // Create new change object
    const change = {
      estate: entity.estate.id,
      model: model.id,
      type: model.type,
      value: value,
      amount: amount,
    };

    // Create new changelist with the update
    setChangeList((prev) => ({
      ...prev,
      [changelistID(entity, model)]: change,
    }));
    setError(null);
  };

  const storeChanges = () => {
    setLoadingState(true);
    setError(null);

    apirequest("post", "/api/billing-attribute-bulk-upsert/", {
      attributes: Object.values(changelist),
    }).then(
      (success) => {
        props.listBillingForecast(props.block);
        setEditing(false);
        setLoadingState(false);
        setChangeList({});
      },
      (err) => {
        setLoadingState(false);
        setError(ErrorWrap.fromError(result.response.body));
      }
    );
  };

  const getBreakdown = (entity, model) => {
    const id = changelistID(entity, model);
    return changelist[id] || entity.breakdown[model.type] || {};
  };

  const getTotals = (entity, models) => {
    let total = 0;
    models.forEach((model) => {
      const breakdown = getBreakdown(entity, model);
      const amount = parseFloat(breakdown.amount);
      if (!isNaN(amount)) {
        total = total + parseFloat(amount.toFixed(2));
      }
    });
    return amountToCurrency(
      noZero(total),
      getAppCurrency(),
      props.selectedCurrency
    );
  };

  const { models, loading: propsLoading } = props;
  let forecast = props.forecast;

  if (!forecast || !models || propsLoading) {
    return <Loader active={true} />;
  }

  if (filterBy) {
    forecast = forecast.filter((x) => x.forecast.hasOwnProperty(filterBy));
  }

  // Calculate the sum of all values for each attribute and the sum of all row totals
  const perAttributeTotal = [];
  let totalsTotal = 0.0;
  forecast.forEach((entity, rowIdx) => {
    models.forEach((model, modelIdx) => {
      const breakdown = getBreakdown(entity, model);
      const value = breakdown.amount;

      if (perAttributeTotal.length <= modelIdx) {
        perAttributeTotal.push(0.0);
      }
      if (value) {
        totalsTotal += Number(value);
        perAttributeTotal[modelIdx] += Number(value);
      }
    });
  });

  return (
    <Segment>
      <Controller>
        <DownloadLink
          href={`/api/billing-forecast?block=${props.block}&format=csv`}
          target={"_blank"}
          download={`${props.block}-` + t("billing.budget") + `.csv`}
          icon={"download"}
          disabled={editing}
        />
        <Button
          size={"tiny"}
          toggle={true}
          disabled={editing}
          active={!displayCalculated && !editing}
          onClick={() => setDisplayCalculated(!displayCalculated)}
          content={t("billing.attributes")}
        />
        <RoleRequired
          admin={true}
          permission={permissions.billingAttributeEdit}
        >
          <Button
            size={"tiny"}
            icon={editing ? "close" : "edit"}
            secondary={editing}
            onClick={() => {
              setDisplayCalculated(editing);
              setEditing(!editing);
              setChangeList({});
            }}
          />
        </RoleRequired>
      </Controller>
      <div style={{ overflow: "auto", maxHeight: 550, margin: "10px 0" }}>
        <Table
          unstackable={true}
          celled={true}
          striped={true}
          selectable={true}
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>
                {t("resources.estate_capital")}
              </Table.HeaderCell>
              {models.map((entry, idx) => {
                return (
                  <Table.HeaderCell key={idx}>
                    {entry.type}
                    <div>{`(${amountToCurrency(
                      noZero(perAttributeTotal[idx]),
                      getAppCurrency(),
                      props.selectedCurrency
                    )})`}</div>
                  </Table.HeaderCell>
                );
              })}
              <Table.HeaderCell>
                {t("billing.full_payment")}
                <div>{`(${amountToCurrency(
                  noZero(totalsTotal),
                  getAppCurrency(),
                  props.selectedCurrency
                )})`}</div>
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {forecast.map((entity, rowIdx) => {
              const totals = getTotals(entity, models);
              if (
                totals !==
                amountToCurrency(
                  noZero(entity.total),
                  getAppCurrency(),
                  props.selectedCurrency
                )
              ) {
              }
              return (
                <Table.Row key={entity.estate.id}>
                  <Table.Cell>
                    <Link
                      tabIndex={editing ? "-1" : "0"}
                      to={reverse(routes.estateDetails, {
                        blockId: entity.estate.block_id,
                        estateId: entity.estate.id,
                      })}
                    >
                      {entity.estate.name}
                    </Link>
                  </Table.Cell>
                  {models.map((model, idx) => {
                    const breakdown = getBreakdown(entity, model);
                    let value = displayCalculated
                      ? amountToCurrencyOrEmpty(
                          breakdown.amount,
                          getAppCurrency(),
                          props.selectedCurrency
                        )
                      : breakdown.value;
                    value = value || "";
                    return (
                      <EditableCell
                        key={idx}
                        mountIdx={`${rowIdx}-${idx}`}
                        value={value}
                        editing={editing}
                        onChange={(value) => {
                          onFieldChange(entity, model, value);
                        }}
                        error={breakdown.error}
                      />
                    );
                  })}
                  <Table.Cell
                    warning={
                      editing &&
                      totals !==
                        amountToCurrency(
                          noZero(entity.total),
                          props.selectedCurrency
                        )
                    }
                  >
                    {totals}
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      </div>
      <Controller>
        {editing && (
          <Button
            size={"tiny"}
            icon={"check"}
            floated={"right"}
            loading={loadingState}
            onClick={storeChanges}
            positive={true}
            content={t("billing.save")}
          />
        )}
      </Controller>
    </Segment>
  );
};

const mapStateToProps = (state) => {
  const models = state.billingModel.entities;
  return {
    forecast: state.billingForecast.entities,
    models: models,
    loading: state.billingForecast.pending || state.billingModel.pending,
    selectedCurrency: state.currency.selectedCurrency,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      listBillingForecast,
    },
    dispatch
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(BudgetList);
